import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type ReactNode,
  type CSSProperties,
  type ComponentPropsWithoutRef,
} from "react";
import { createPortal } from "react-dom";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";

type FrameProps = Omit<ComponentPropsWithoutRef<"iframe">, "children"> & {
  children: ReactNode;
  head: ReactNode;
  style?: CSSProperties;
};

export default function Frame({
  children,
  head,
  title = "",
  style = {},
  ...rest
}: FrameProps) {
  const nodeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHead, setIframeHead] = useState<HTMLHeadElement | null>(null);
  const [iframeRoot, setIframeRoot] = useState<HTMLElement | null>(null);

  const emotionCache = useMemo(
    () =>
      iframeHead ? createCache({ key: "iframe", container: iframeHead }) : null,
    [iframeHead],
  );

  useEffect(() => {
    const node = nodeRef.current;

    const handleLoad = () => {
      if (node?.contentDocument) {
        setIframeHead(node.contentDocument.head);
        setIframeRoot(node.contentDocument.body);
      }
    };

    node?.addEventListener("load", handleLoad);
    return () => node?.removeEventListener("load", handleLoad);
  }, []);

  return (
    <iframe
      srcDoc="<!DOCTYPE html>"
      {...rest}
      ref={nodeRef}
      title={title}
      style={{ border: "none", ...style }}
    >
      {iframeHead && createPortal(head, iframeHead)}
      {iframeRoot &&
        emotionCache &&
        createPortal(
          <CacheProvider value={emotionCache}>{children}</CacheProvider>,
          iframeRoot,
        )}
    </iframe>
  );
}
